import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { ImagePlus, Loader2, Trash2 } from "lucide-react";
import { api, formatApiError } from "../../api";
import { MARKETING } from "../../constants/testIds";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { toast } from "../ui/sonner";
import { AdPreview } from "./AdPreview";

const CTAS = ["Learn More", "Sign Up Now", "Get Offer", "Book Now", "Claim Trial", "Join Challenge", "Contact Us"];

// Editing what the ad actually says, and attaching the gym's own photo or video to it.
// Every change re-renders the preview beside it, so there's no guessing what will go out.
export function CreativeEditor({ creatives, platform, gymName, onChange, onSave, saving }) {
  const [index, setIndex] = useState(0);
  const fileRef = useRef(null);
  const creative = creatives[index] || {};

  const uploadMutation = useMutation({
    mutationFn: async (file) => {
      const body = new FormData();
      body.append("file", file);
      return (await api.post("/marketing/creatives/upload", body, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: (data) => {
      // A video asset implies a video ad — keep the declared format honest about what's attached.
      update({ asset_key: data.asset_key, format: data.kind === "video" ? "video" : "image" });
      toast.success(`${data.kind === "video" ? "Video" : "Image"} attached.`);
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const update = (patch) => {
    const next = creatives.map((c, i) => (i === index ? { ...c, ...patch } : c));
    onChange(next);
  };

  const addCreative = () => {
    onChange([...creatives, { variant: String.fromCharCode(65 + creatives.length), format: "image", headline: "", body: "", cta: "Learn More" }]);
    setIndex(creatives.length);
  };

  const removeCreative = () => {
    const next = creatives.filter((_, i) => i !== index);
    onChange(next);
    setIndex(Math.max(0, index - 1));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1.5 flex-wrap">
        {creatives.map((c, i) => (
          <button
            key={i} onClick={() => setIndex(i)}
            className="px-2.5 py-1 rounded-[8px] text-[11px] font-semibold"
            style={index === i
              ? { background: "var(--scarlet-accent)", color: "#fff" }
              : { background: "var(--scarlet-surface)", color: "var(--scarlet-text-secondary)", border: "1px solid var(--scarlet-hairline)" }}
          >
            {c.variant ? `Variant ${c.variant}` : `Ad ${i + 1}`}
          </button>
        ))}
        <Button size="sm" variant="outline" onClick={addCreative} data-testid={MARKETING.creativeAdd}>+ Add ad</Button>
        {creatives.length > 1 && (
          <Button size="sm" variant="outline" onClick={removeCreative} className="gap-1"
            style={{ color: "var(--scarlet-neg)", borderColor: "var(--scarlet-neg)" }}>
            <Trash2 size={12} /> Remove
          </Button>
        )}
      </div>

      {!!creatives.length && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Headline</Label>
              <Input data-testid={MARKETING.creativeHeadline} value={creative.headline || ""}
                onChange={(e) => update({ headline: e.target.value })} placeholder="Free Trial At Our Gym!" />
            </div>
            <div className="space-y-1.5">
              <Label>Ad copy</Label>
              <Textarea data-testid={MARKETING.creativeBody} rows={3} value={creative.body || ""}
                onChange={(e) => update({ body: e.target.value })} placeholder="Try our gym for 7 days free…" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Button</Label>
                <select data-testid={MARKETING.creativeCta} value={creative.cta || "Learn More"} onChange={(e) => update({ cta: e.target.value })}
                  className="w-full h-10 rounded-[8px] px-3 text-sm"
                  style={{ border: "1px solid var(--scarlet-hairline)", background: "var(--scarlet-surface)", color: "var(--scarlet-text)" }}>
                  {CTAS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>Link</Label>
                <Input data-testid={MARKETING.creativeUrl} value={creative.landing_url || ""}
                  onChange={(e) => update({ landing_url: e.target.value })} placeholder="yourgym.com/trial" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Photo or video</Label>
              <input
                ref={fileRef} type="file" accept="image/*,video/mp4,video/quicktime" className="hidden"
                data-testid={MARKETING.creativeFileInput}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadMutation.mutate(f); e.target.value = ""; }}
              />
              <div className="flex gap-2">
                <Button variant="outline" className="gap-1.5" disabled={uploadMutation.isPending}
                  data-testid={MARKETING.creativeUploadButton} onClick={() => fileRef.current?.click()}>
                  {uploadMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
                  {creative.asset_key ? "Replace" : "Upload"}
                </Button>
                {creative.asset_key && (
                  <Button variant="outline" className="gap-1.5" onClick={() => update({ asset_key: null })}
                    style={{ color: "var(--scarlet-neg)", borderColor: "var(--scarlet-neg)" }}>
                    <Trash2 size={13} /> Remove
                  </Button>
                )}
              </div>
              <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>
                JPG, PNG, WEBP or GIF up to 8MB · MP4/MOV up to 100MB. Meta recommends 1200×628 for feed images.
              </p>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold mb-1.5" style={{ color: "var(--scarlet-text-secondary)" }}>Live preview</p>
            <AdPreview creatives={[creative]} platform={platform} gymName={gymName} />
          </div>
        </div>
      )}

      <Button data-testid={MARKETING.creativeSave} disabled={saving} onClick={onSave} className="text-white" style={{ background: "var(--scarlet-accent)" }}>
        {saving ? "Saving…" : "Save ads"}
      </Button>
    </div>
  );
}
